import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { Search, X, Building2, User, Inbox, Flag, HeartHandshake } from "lucide-react";

/**
 * Recherche globale Admin (Phase 10 v2.0.01).
 * Recherche par PAG-CLI / PAG-ENT / SIRET / raison sociale / email / téléphone.
 * Résultats en dropdown popover, click = navigation vers la fiche adéquate.
 */
export default function AdminGlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q.trim())}&limit=6`);
        setResults(data);
        setOpen(true);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  const total = results ? Object.values(results.counts || {}).reduce((a, b) => a + b, 0) : 0;

  const entreprises = results?.entreprises || [];
  const users = results?.users || [];
  const inscriptions = results?.inscriptions || [];
  const syndicats = results?.syndicats || [];
  const associations = results?.associations || [];

  return (
    <div ref={boxRef} className="relative w-full max-w-md" data-testid="admin-global-search">
      <div className="flex items-center gap-2 border border-[#DEE2E6] bg-white px-3 h-10 focus-within:border-[#002FA7]">
        <Search className="h-4 w-4 text-[#868E96] flex-shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.length >= 2 && setOpen(true)}
          data-testid="global-search-input"
          placeholder="PAG-CLI / PAG-ENT / SIRET / nom / email…"
          className="flex-1 outline-none text-sm bg-transparent"
        />
        {q && (
          <button onClick={() => { setQ(""); setResults(null); setOpen(false); }} className="p-0.5 hover:bg-[#F8F9FA]" data-testid="global-search-clear">
            <X className="h-3.5 w-3.5 text-[#868E96]" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#0A0A0A] shadow-lg z-50 max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {loading && <div className="p-4 text-xs text-[#868E96]">Recherche…</div>}
          {!loading && total === 0 && (
            <div className="p-6 text-center text-sm text-[#868E96]" data-testid="global-search-empty">
              Aucun résultat pour <b>&quot;{q}&quot;</b>
            </div>
          )}
          {!loading && total > 0 && (
            <>
              {entreprises.length > 0 && (
                <Section title="Entreprises" icon={Building2} count={entreprises.length}>
                  {entreprises.map((e) => (
                    <button key={e.id} onClick={() => go(`/admin/entreprises/${e.id}`)} data-testid={`gsearch-ent-${e.code}`}
                      className="w-full text-left p-3 hover:bg-[#F8F9FA] border-b border-[#DEE2E6] last:border-b-0">
                      <div className="font-semibold text-sm">{e.name}</div>
                      <div className="text-[10px] font-mono text-[#495057] flex gap-3 mt-0.5">
                        <span className="text-[#002FA7]">{e.code}</span>
                        {e.pag_cli && <span className="text-[#059669]">{e.pag_cli}</span>}
                        {e.siret && <span className="text-[#868E96]">SIRET {e.siret}</span>}
                      </div>
                    </button>
                  ))}
                </Section>
              )}
              {users.length > 0 && (
                <Section title="Utilisateurs" icon={User} count={users.length}>
                  {users.map((u) => (
                    <div key={u.id} className="p-3 border-b border-[#DEE2E6] last:border-b-0" data-testid={`gsearch-user-${u.code}`}>
                      <div className="text-sm">{u.prenom} {u.nom} <span className="text-[10px] uppercase text-[#868E96] font-bold ml-1">· {u.role}</span></div>
                      <div className="text-[10px] font-mono text-[#495057]">{u.code} · {u.email || "—"}</div>
                    </div>
                  ))}
                </Section>
              )}
              {inscriptions.length > 0 && (
                <Section title="Inscriptions" icon={Inbox} count={inscriptions.length}>
                  {inscriptions.map((i) => (
                    <button key={i.id} onClick={() => go("/admin/inscriptions")} data-testid={`gsearch-ins-${i.id}`}
                      className="w-full text-left p-3 hover:bg-[#F8F9FA] border-b border-[#DEE2E6] last:border-b-0">
                      <div className="text-sm font-semibold">{i.raison_sociale}</div>
                      <div className="text-[10px] font-mono text-[#495057]">{i.email} · {i.telephone}</div>
                    </button>
                  ))}
                </Section>
              )}
              {syndicats.length > 0 && (
                <Section title="Syndicats" icon={Flag} count={syndicats.length}>
                  {syndicats.map((s) => (
                    <div key={s.id} className="p-3 border-b border-[#DEE2E6] last:border-b-0" data-testid={`gsearch-synd-${s.code}`}>
                      <div className="text-sm font-semibold">{s.name}</div>
                      <div className="text-[10px] font-mono">{s.code} · {s.email}</div>
                    </div>
                  ))}
                </Section>
              )}
              {associations.length > 0 && (
                <Section title="Associations / ONG" icon={HeartHandshake} count={associations.length}>
                  {associations.map((a) => (
                    <div key={a.id} className="p-3 border-b border-[#DEE2E6] last:border-b-0" data-testid={`gsearch-asso-${a.code}`}>
                      <div className="text-sm font-semibold">{a.name}</div>
                      <div className="text-[10px] font-mono">{a.code} · {a.email}</div>
                    </div>
                  ))}
                </Section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div>
      <div className="px-3 py-2 bg-[#0A0A0A] text-white flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold">
        <Icon className="h-3 w-3" /> {title} <span className="opacity-60">({count})</span>
      </div>
      {children}
    </div>
  );
}
